import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	Button,
	Heading,
	Link as RLink,
	Text,
	Flex,
	Box,
} from '@radix-ui/themes'
import { Link, useFetcher, useSearchParams } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { FormDefaults } from '~/components/common/form-defaults.tsx'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { TextField, type TextFieldProps } from '~/components/ui/text-field.tsx'
import { type Locale } from '~/models/locale.ts'
import { Alert } from '~/routes/($lang)._auth.login/alert.tsx'
import {
	LOGIN_I18NEXT,
	LOGIN_FORM,
	LOGIN_INPUTS,
	loginSchema,
	LOGIN_SEARCH_PARAMS,
} from '~/routes/($lang)._auth.login/login.component.tsx'
import { useLoginNotify } from '~/routes/($lang)._auth.login/use-login-notify.tsx'
import {
	appView,
	ROOT_INPUTS,
	type App,
} from '~/routes/_layout/root.component.tsx'

/* -------------------------------------------------------------------------------------------------
 * LoginForm
 * -----------------------------------------------------------------------------------------------*/

type LoginFormProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const LoginForm: React.FC<LoginFormProps> = (props) => {
	const { app, locale, setApp } = props

	const fetcher = useFetcher({ key: LOGIN_FORM.fetcherKey })
	const [searchParams] = useSearchParams()
	const { t } = useTranslation(LOGIN_I18NEXT.routeAuthLogin)
  const formValues = {
    [ROOT_INPUTS.subaction]: LOGIN_FORM.subaction,
    [ROOT_INPUTS.noJs]: String(app.options.noJs || true),
    ...(app.options.redirect && {
      [ROOT_INPUTS.redirect]: app.options.redirect,
    }),
  }
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			...formValues,
			[LOGIN_INPUTS.email]:
				searchParams.get(LOGIN_SEARCH_PARAMS.email) ||
				app.options.data?.[LOGIN_INPUTS.email] ||
				undefined,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: loginSchema })
		},
		shouldValidate: 'onInput',
	})

	useLoginNotify({ app, setApp, fetcher, t })

	return (
		<FormProvider context={form.context}>
			<fetcher.Form
				method="post"
				action={`/${locale}${LOGIN_FORM.action}?${searchParams}`}
				{...getFormProps(form)}
				{...(app.options?.preventScrollReset && { preventScrollReset: true })}
			>
				<FormDefaults
          form={form}
          fields={fields}
          values={formValues}
        />
				<Heading align="center" size="8" color="green">
					{t('common.title')}
				</Heading>
				<Box mt="3">
					<Alert t={t} searchParams={searchParams} fetcher={fetcher} />
				</Box>
				<Flex direction="column" gapY="6" mt="8">
					{[
						{
							title: LOGIN_INPUTS.email,
							type: 'email',
							validations: {
								required: true,
							},
						},
						{
							title: LOGIN_INPUTS.password,
							type: 'password',
							validations: {
								required: true,
								minLength: 4,
							},
						},
					].map(({ title, type, validations }) => (
						<TextField
							key={title}
							type={type as TextFieldProps['type']}
							meta={fields[title]}
							t={t}
							validations={validations}
						>
							{t(`label.${type}`)}
						</TextField>
					))}
					<Flex direction="column" gap="3" mt="3">
						<Flex justify="end">
							<Link
								to={`/${locale}/reset-password${fields[LOGIN_INPUTS['email']].value ? `?email=${fields[LOGIN_INPUTS['email']].value}` : ''}`}
								prefetch="intent"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: appView.resetPasswordView,
											options: {
												...prev.options,
												data: {
													[LOGIN_INPUTS.email]:
														fields[LOGIN_INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.resetPassword')}</span>
								</RLink>
							</Link>
						</Flex>
						<Button size="3" type="submit">
							<SubmissionButton fetcher={fetcher}>
								{t('button.loginSubmit')}
							</SubmissionButton>
						</Button>
						<Flex align="center" justify="center" gapX="2" mt="4">
							<Text color="gray">{t('common.registration')}</Text>
							<Link
								to={`/${locale}/registration${fields[LOGIN_INPUTS['email']].value ? `?email=${fields[LOGIN_INPUTS['email']].value}` : ''}`}
								prefetch="intent"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: appView.registrationView,
											options: {
												...prev.options,
												data: {
													[LOGIN_INPUTS.email]:
														fields[LOGIN_INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.registration')}</span>
								</RLink>
							</Link>
						</Flex>
					</Flex>
				</Flex>
			</fetcher.Form>
		</FormProvider>
	)
}
LoginForm.displayName = 'LoginForm'

/* -----------------------------------------------------------------------------------------------*/

export { type LoginFormProps, LoginForm }
